import * as React from 'react';
import { CSSTransition } from 'react-transition-group';
import { Async, autobind, css } from 'office-ui-fabric-react/lib/Utilities';
import * as LayoutSwitcherStyles from './LayoutSwitcher.scss';

export interface ILayoutSwitcherProps {
  layoutKey: string | undefined;
}

export interface ILayoutSwitcherState {
  previousLayout?: React.ReactNode;
  previousLayoutKey?: string;
  transitionPreviousLayout?: boolean;
  transitionCurrentLayout?: boolean;
  previousLayoutClasses?: CSSTransition.CSSTransitionClassNames;
  currentLayoutClasses?: CSSTransition.CSSTransitionClassNames;
}

export class LayoutSwitcher extends React.PureComponent<ILayoutSwitcherProps, ILayoutSwitcherState> {
  private static _growOutAnimationClassNames: CSSTransition.CSSTransitionClassNames = {
    enter: LayoutSwitcherStyles.enterAnimationGrowOut,
    exit: LayoutSwitcherStyles.exitAnimationGrowOut,
  };

  private static _shrinkInAnimationClassNames: CSSTransition.CSSTransitionClassNames = {
    enter: LayoutSwitcherStyles.enterAnimationShrinkIn,
    exit: LayoutSwitcherStyles.exitAnimationShrinkIn,
  };

  private static _initialState: ILayoutSwitcherState = {
    previousLayoutKey: undefined,
    previousLayout: undefined,
    transitionPreviousLayout: true,
    transitionCurrentLayout: true
  };

  private static _previousLayoutRef: HTMLDivElement | null;
  private static _currentLayoutRef: HTMLDivElement | null;
  private _async: Async;

  constructor(props: ILayoutSwitcherProps) {
    super(props);

    this._async = new Async(this);

    this.state = LayoutSwitcher._initialState;
  }

  public render(): JSX.Element {
    const {
      transitionPreviousLayout,
      transitionCurrentLayout,
      previousLayoutClasses,
      currentLayoutClasses,
      previousLayout,
      previousLayoutKey
    } = this.state;

    return (
      <div
        className={
          css(
            LayoutSwitcherStyles.layoutPositiningContainer,
            !transitionPreviousLayout && LayoutSwitcherStyles.layoutPositiningContainerTransitioning
          )
        }
      >
        { previousLayout && previousLayoutKey ? (
          // This component only shows the exit animation.
          <CSSTransition
            key={ previousLayoutKey }
            timeout={ {
              enter: 0,
              exit: 200
            } }
            in={ transitionPreviousLayout }
            classNames={ previousLayoutClasses ? previousLayoutClasses : LayoutSwitcher._growOutAnimationClassNames }
            onExited={ this._onPreviousLayoutExited }
          >
            <div
              className={ LayoutSwitcherStyles.layoutSwitcherContainer }
              ref={ (el: HTMLDivElement) => { LayoutSwitcher._previousLayoutRef = el; } }
            >
              { previousLayout }
            </div>
          </CSSTransition>
        ) : null }
        { this.props.children && this.props.layoutKey ? (
          // This component only shows the enterance animation. It gets stored as previousLayout to exit as we receive the incoming layout.
          <CSSTransition
            key={ this.props.layoutKey }
            timeout={ {
              enter: 200,
              exit: 0
            } }
            in={ transitionCurrentLayout }
            classNames={ currentLayoutClasses ? currentLayoutClasses : LayoutSwitcher._growOutAnimationClassNames }
          >
            <div
              className={
                css(
                  previousLayout && LayoutSwitcherStyles.hideLayout,
                  LayoutSwitcherStyles.layoutSwitcherContainer
                )
              }
              ref={ (el: HTMLDivElement) => { LayoutSwitcher._currentLayoutRef = el; } }
            >
              { this.props.children }
            </div>
          </CSSTransition>
        ) : null }
      </div>
    );
  }

  public componentWillReceiveProps(nextProps: ILayoutSwitcherProps): void {
    if (!this.state.previousLayoutKey && this.props.layoutKey &&
      nextProps.layoutKey !== this.props.layoutKey) {
      // If we're getting a new layout, and we're not transitioning, snapshot the current layout and store it in state.
      this.setState({
        previousLayoutKey: this.props.layoutKey,
        previousLayout: this.props.children,
        previousLayoutClasses: undefined,
        currentLayoutClasses: undefined
      });
    } else if (this.state.previousLayoutKey && nextProps.layoutKey && this.state.previousLayoutKey === nextProps.layoutKey) {
      // If we switch layouts before an animation is done, reset the state so we don't get a duplicate key / component.
      this.setState(LayoutSwitcher._initialState);
    }
  }

  public componentDidUpdate(prevProps: ILayoutSwitcherProps, prevState: ILayoutSwitcherState): void {
    if (!prevState.previousLayoutKey && this.state.previousLayoutKey) {
      // Layout has changed, start the transition.
      this._async.setTimeout(() => {
        // Without the timeout, clientHeight is always 0.
        if (LayoutSwitcher._previousLayoutRef && LayoutSwitcher._currentLayoutRef) {
          this._determineTransitionDirection();
          this.setState({
            transitionCurrentLayout: false
          });
        }
      }, 0);
      this.setState({
        transitionPreviousLayout: false
      });
    }
  }

  public componentWillUnmount(): void {
    this._async.dispose();
  }

  @autobind
  private _determineTransitionDirection(): void {
    const previousLayoutRef = LayoutSwitcher._previousLayoutRef;
    const currentLayoutRef = LayoutSwitcher._currentLayoutRef;
    const previousLayoutHeight = previousLayoutRef && previousLayoutRef.clientHeight;
    const currentLayoutHeight = currentLayoutRef && currentLayoutRef.clientHeight;

    if (!previousLayoutHeight || !currentLayoutHeight) {
      return;
    }

    if (previousLayoutHeight! <= currentLayoutHeight!) {
      this.setState({
        previousLayoutClasses: LayoutSwitcher._growOutAnimationClassNames,
        currentLayoutClasses: LayoutSwitcher._shrinkInAnimationClassNames
      });
    } else {
      this.setState({
        previousLayoutClasses: LayoutSwitcher._shrinkInAnimationClassNames,
        currentLayoutClasses: LayoutSwitcher._growOutAnimationClassNames
      });
    }
  }

  @autobind
  private _onPreviousLayoutExited(): void {
    this.setState(LayoutSwitcher._initialState);
  }
}